import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Pagination,
  SearchInput,
  Tag,
  TagAppearance,
} from "@hid-galaxy-ui/galaxy-react";
import IntegrationColumnsDropdown from "./IntegrationColumnsDropdown";
import TechnicalConsultantInfo from "../../../../../../common/components/TechnicalConsultantInfo/TechnicalConsultantInfo";
import { formatIntegrationColumnLabel } from "./integrationStatusColumns";
import {
  IntegrationCellStatus,
  IntegrationCurrentStatus,
  INTEGRATION_SUMMARY_TILE_FILTER_STATUS,
  IntegrationPartnerRow,
  IntegrationPlatformCard,
  IntegrationPlatformDetail,
  IntegrationPlatformProgress,
  IntegrationProjectDetail,
  NOT_APPLICABLE_PROGRESS,
} from "./integrationStatusTypes";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { getPartnerIntegrationStatusSagaAction, exportPartnerIntegrationStatusSagaAction } from "../../../../../../redux/saga/integration-status/saga-actions";
import {
  useSessionCanAccessModule,
  useSessionModuleReadOnly,
} from "../../../../../../hooks/useSessionPermissions";
import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../../../../../utils/permissions/partnerManagementPermissionKeys";
import IntegrationTimeline from "../../../../../../common/components/integration-timeline/IntegrationTimeline";
import IntegrationFilterModal from "./integration-filter-modal";
import {
  EMPTY_INTEGRATION_FILTER,
  hasActiveIntegrationFilters,
  IntegrationFilterState,
} from "./integration-filter-modal/integrationFilterTypes";
import { buildIntegrationStatusRequest } from "./buildIntegrationStatusRequest";
import { buildIntegrationFilterTags } from "./integrationFilterTags";
import { useDashboardPermissions } from "../../../../../../features/partner-dashboard/hooks/useDashboardPermissions";
import { getPartnerProjectDetailsPath } from "../../../../../../features/partner-dashboard/utils/projectNavigation";
import "./IntegrationStatusTab.scss";

const DEFAULT_INTEGRATION_STATUS_PAGE_SIZE = 20;
/** Delay before a typed search term is sent to the backend. */
const INTEGRATION_STATUS_SEARCH_DEBOUNCE_MS = 350;

const STATUS_LABELS: Record<IntegrationCellStatus, string> = {
  completed: "Completed",
  "not-completed": "Not Completed",
  "not-applicable": "Not Applicable",
};

const IntegrationPlatformProgressCell: React.FC<{
  progress: IntegrationPlatformProgress;
}> = ({ progress }) => {
  if (progress.status === "not-applicable") {
    return (
      <div className={`integration-status-cell integration-status-cell--${progress.status}`}>
        <Iconography
          icon="circleExclamation"
          size={IconographySizes.Small}
          className="integration-status-cell__icon"
          aria-hidden
        />
        <span className="integration-status-cell__text">{STATUS_LABELS[progress.status]}</span>
      </div>
    );
  }

  if (progress.displayLabel) {
    return (
      <div
        className={`integration-status-cell integration-status-cell--${progress.status} integration-status-cell--fraction`}
      >
        <span className="integration-status-cell__text">{progress.displayLabel}</span>
      </div>
    );
  }

  const fraction =
    progress.completedCount != null && progress.totalCount != null
      ? `${progress.completedCount}/${progress.totalCount} `
      : "";

  return (
    <div
      className={`integration-status-cell integration-status-cell--${progress.status} integration-status-cell--fraction`}
    >
      <span className="integration-status-cell__text">
        {fraction}
        {STATUS_LABELS[progress.status]}
      </span>
    </div>
  );
};

const IntegrationStatusTab: React.FC<{
  onOpenPartner?: (partnerId: number) => void;
  canOpenPartner?: boolean;
}> = ({ onOpenPartner, canOpenPartner = false }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { viewAllProjectsEnabled } = useDashboardPermissions("admin");
  const { summaryTiles, platformCards, tableColumns, partners, pagination, status } =
    useAppSelector((state) => state.integration_status);
  const { userListHID } = useAppSelector((state) => state.user);

  const canAccessIntegrationStatus = useSessionCanAccessModule(
    PM_KEYS.ADMIN_PARTNER_INTEGRATION_STATUS
  );
  const integrationStatusReadOnly = useSessionModuleReadOnly(
    PM_KEYS.ADMIN_PARTNER_INTEGRATION_STATUS
  );

  const [searchTerm, setSearchTerm] = React.useState("");
  const [appliedSearch, setAppliedSearch] = React.useState("");
  const [currentPageNumber, setCurrentPageNumber] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_INTEGRATION_STATUS_PAGE_SIZE);
  const [expandedPartnerIds, setExpandedPartnerIds] = React.useState<Set<string>>(
    () => new Set()
  );
  const [visibleColumnKeys, setVisibleColumnKeys] = React.useState<string[]>([]);
  const [filterModalOpen, setFilterModalOpen] = React.useState(false);
  const [integrationFilter, setIntegrationFilter] =
    React.useState<IntegrationFilterState>(EMPTY_INTEGRATION_FILTER);

    const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
  "asc"
);

const [isSorted, setIsSorted] = React.useState(false);

  /**
   * Platform card selection is a view of the technology filter, so the cards,
   * the visible columns and the drawer's Technology checkboxes always agree.
   * Keys follow column order rather than the order values were picked.
   */
  const selectedTechnologyKeys = React.useMemo(() => {
    if (integrationFilter.technology.length === 0) return [];

    const selectedLabels = new Set(
      integrationFilter.technology.map((label) => label.trim().toLowerCase())
    );

    return tableColumns
      .filter((col) => selectedLabels.has(col.label.trim().toLowerCase()))
      .map((col) => col.key);
  }, [integrationFilter.technology, tableColumns]);
  const cardsSyncRef = React.useRef<HTMLDivElement>(null);
  const sliderTrackRef = React.useRef<HTMLDivElement>(null);
  const [platformCardWidth, setPlatformCardWidth] = React.useState<number | null>(
    null
  );
  const [sliderAtStart, setSliderAtStart] = React.useState(true);
  const [sliderAtEnd, setSliderAtEnd] = React.useState(false);
  const [sliderHasOverflow, setSliderHasOverflow] = React.useState(false);

  const handleViewPartnerProjects = React.useCallback(
    (partnerId: number) => {
      if (!viewAllProjectsEnabled) return;
      if (!partnerId || Number.isNaN(partnerId)) return;
      navigate(`/partner/${partnerId}/dashboard/projects`);
    },
    [navigate, viewAllProjectsEnabled]
  );

  const handleViewPartnerProject = React.useCallback(
    (partnerId: number, projectName: string, technology?: string) => {
      if (!viewAllProjectsEnabled) return;
      if (!partnerId || Number.isNaN(partnerId) || !projectName) return;

      navigate(getPartnerProjectDetailsPath(partnerId, projectName), {
        state: technology ? { selectedTechnology: technology } : undefined,
      });
    },
    [navigate, viewAllProjectsEnabled]
  );

  const updatePlatformCardWidth = React.useCallback(() => {
    const container = cardsSyncRef.current;
    if (!container) return;
    const firstSummaryCard = container.querySelector<HTMLElement>(
      ".partner-summary-card"
    );
    if (!firstSummaryCard) return;
    const width = Math.round(firstSummaryCard.getBoundingClientRect().width);
    if (width > 0) setPlatformCardWidth(width);
  }, []);

  React.useEffect(() => {
    updatePlatformCardWidth();
    const container = cardsSyncRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => updatePlatformCardWidth());
    observer.observe(container);
    window.addEventListener("resize", updatePlatformCardWidth);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePlatformCardWidth);
    };
  }, [updatePlatformCardWidth]);

  const checkSliderScroll = React.useCallback(() => {
    const el = sliderTrackRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 2;
    setSliderHasOverflow(overflow);
    setSliderAtStart(el.scrollLeft <= 1);
    setSliderAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 2);
  }, []);

  React.useLayoutEffect(() => {
    const el = sliderTrackRef.current;
    if (!el) return;

    const runCheck = () => {
      requestAnimationFrame(checkSliderScroll);
    };

    runCheck();
    el.addEventListener("scroll", checkSliderScroll);
    const observer = new ResizeObserver(runCheck);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", checkSliderScroll);
      observer.disconnect();
    };
  }, [checkSliderScroll, platformCardWidth, platformCards.length]);

  const hadTechnologySelectionRef = React.useRef(false);

  React.useEffect(() => {
    if (tableColumns.length === 0) return;

    const availableKeys = tableColumns.map((col) => col.key);
    const hadTechnologySelection = hadTechnologySelectionRef.current;
    hadTechnologySelectionRef.current = selectedTechnologyKeys.length > 0;

    if (selectedTechnologyKeys.length > 0) {
      setVisibleColumnKeys(selectedTechnologyKeys);
      return;
    }

    // Clearing the chips/cards must undo the narrowing they applied.
    if (hadTechnologySelection) {
      setVisibleColumnKeys(availableKeys);
      return;
    }

    setVisibleColumnKeys((prev) => {
      const valid = prev.filter((key) => availableKeys.includes(key));
      return valid.length > 0 ? valid : availableKeys;
    });
  }, [tableColumns, selectedTechnologyKeys]);

  /** Mirrors toggling the matching Technology checkbox in the filter drawer. */
  const handlePlatformCardClick = (cardId: string) => {
    const column = tableColumns.find((col) => col.key === cardId);
    if (!column) return;

    const matchesColumn = (label: string) =>
      label.trim().toLowerCase() === column.label.trim().toLowerCase();

    setIntegrationFilter((prev) => ({
      ...prev,
      technology: prev.technology.some(matchesColumn)
        ? prev.technology.filter((label) => !matchesColumn(label))
        : [...prev.technology, column.label],
    }));
    setCurrentPageNumber(1);
  };

  const handleVisibleColumnKeysChange = (keys: string[]) => {
    setVisibleColumnKeys(keys);
  };

  const hasActiveFilters = hasActiveIntegrationFilters(integrationFilter);

  const consultantLabelById = React.useMemo(
    () =>
      Object.fromEntries(
        userListHID.map((user) => [
          String(user.ID),
          `${user.first_name} ${user.last_name}`.trim(),
        ])
      ),
    [userListHID]
  );

  const integrationFilterTags = React.useMemo(
    () => buildIntegrationFilterTags(integrationFilter, consultantLabelById),
    [integrationFilter, consultantLabelById]
  );

  const handleApplyIntegrationFilter = React.useCallback(
    (nextFilter: IntegrationFilterState) => {
      setIntegrationFilter(nextFilter);
      setCurrentPageNumber(1);
    },
    []
  );

  const handleRemoveIntegrationFilterTag = React.useCallback(
    (tagKey: string) => {
      const tag = integrationFilterTags.find((item) => item.key === tagKey);
      if (!tag) return;

      setIntegrationFilter(tag.remove(integrationFilter));
      setCurrentPageNumber(1);
    },
    [integrationFilter, integrationFilterTags]
  );

  const activeSummaryTileId = React.useMemo(() => {
    if (integrationFilter.validationStatus.length === 0) {
      return "overall";
    }

    if (integrationFilter.validationStatus.length !== 1) {
      return null;
    }

    const selectedStatus = integrationFilter.validationStatus[0];
    const matchedTile = Object.entries(INTEGRATION_SUMMARY_TILE_FILTER_STATUS).find(
      ([, statusValue]) => statusValue === selectedStatus
    );

    return matchedTile?.[0] ?? null;
  }, [integrationFilter.validationStatus]);

    const sortedPartners = React.useMemo(() => {
  if (!isSorted) {
    return partners;
  }

  return [...partners].sort((a, b) =>
    sortDirection === "asc"
      ? a.partnerName.localeCompare(b.partnerName)
      : b.partnerName.localeCompare(a.partnerName)
  );
}, [partners, sortDirection, isSorted]);

  const handleSummaryTileClick = React.useCallback(
    (tileId: string) => {
      const filterValue = INTEGRATION_SUMMARY_TILE_FILTER_STATUS[tileId];

      if (tileId === "overall" || filterValue === null) {
        setIntegrationFilter((prev) => ({
          ...prev,
          validationStatus: [],
        }));
        setCurrentPageNumber(1);
        return;
      }

      const isCurrentlySelected =
        integrationFilter.validationStatus.length === 1 &&
        integrationFilter.validationStatus[0] === filterValue;

      setIntegrationFilter((prev) => ({
        ...prev,
        validationStatus: isCurrentlySelected ? [] : [filterValue],
      }));
      setCurrentPageNumber(1);
    },
    [integrationFilter.validationStatus]
  );

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setAppliedSearch(searchTerm.trim());
      setCurrentPageNumber(1);
    }, INTEGRATION_STATUS_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  React.useEffect(() => {
    dispatch(
      getPartnerIntegrationStatusSagaAction(
        buildIntegrationStatusRequest(
          integrationFilter,
          appliedSearch,
          currentPageNumber,
          pageSize
        )
      )
    );
    setExpandedPartnerIds(new Set());
  }, [
    dispatch,
    currentPageNumber,
    pageSize,
    appliedSearch,
    integrationFilter,
  ]);

  const handleSearch = (_event: unknown, value: string) => {
    setSearchTerm(value);
    setAppliedSearch(value.trim());
    setCurrentPageNumber(1);
  };

  const handleExport = () => {
    if (status.exportLoading || integrationStatusReadOnly) return;
    dispatch(
      exportPartnerIntegrationStatusSagaAction(
        buildIntegrationStatusRequest(
          integrationFilter,
          appliedSearch,
          currentPageNumber,
          pageSize
        )
      )
    );
  };

  const handlePageChange = (clickedPageNumber: number | null) => {
    if (clickedPageNumber != null) {
      setCurrentPageNumber(clickedPageNumber);
      setExpandedPartnerIds(new Set());
    }
  };

  const onPageSizesClick = (_event: unknown, value: string) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  const togglePartnerExpanded = (partnerId: string) => {
    setExpandedPartnerIds((prev) => {
      const next = new Set(prev);
      if (next.has(partnerId)) next.delete(partnerId);
      else next.add(partnerId);
      return next;
    });
  };

  const scrollPlatformSlider = (direction: 1 | -1) => {
    const track = sliderTrackRef.current;
    if (!track) return;
    const gap = 16;
    const firstCard = track.querySelector<HTMLElement>(".integration-platform-card");
    const cardWidth =
      platformCardWidth ??
      firstCard?.getBoundingClientRect().width ??
      (track.clientWidth - gap * 3) / 4;
    track.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: "smooth",
    });
  };

  const integrationStatusStyle = platformCardWidth
    ? ({
        ["--integration-platform-card-width" as string]: `${platformCardWidth}px`,
      } as React.CSSProperties)
    : undefined;

  const isInitialLoad = status.loading && summaryTiles.length === 0;
  const showTableLoader = status.loading;

  if (isInitialLoad) {
    return (
      <div className="integration-status integration-status--loading">
        <Loader variant={LoaderSizes.large} />
      </div>
    );
  }



const handlePartnerNameSort = () => {
  if (!isSorted) {
    setIsSorted(true);
    setSortDirection("asc");
    return;
  }

  setSortDirection((prev) =>
    prev === "asc" ? "desc" : "asc"
  );
};

const getSortIcon = () => {
  if (!isSorted) {
    return (
      <Iconography
        icon="sort"
        size={IconographySizes.Small}
      />
    );
  }

  return sortDirection === "asc" ? (
    <Iconography
      icon="chevronUp"
      size={IconographySizes.Small}
    />
  ) : (
    <Iconography
      icon="chevronDown"
      size={IconographySizes.Small}
    />
  );
};

  return (
    <div className="integration-status" ref={cardsSyncRef} style={integrationStatusStyle}>
      <div className="integration-cards-sync">
        <div className="partner-summary-cards">
          {summaryTiles.map((tile) => (
            <button
              type="button"
              className={`partner-summary-card${
                activeSummaryTileId === tile.id
                  ? " partner-summary-card--selected"
                  : ""
              }`}
              key={tile.id}
              onClick={() => handleSummaryTileClick(tile.id)}
              disabled={integrationStatusReadOnly}
              aria-pressed={activeSummaryTileId === tile.id}
            >
              <span className="partner-summary-card__label">{tile.label}</span>
              <span className="partner-summary-card__footer">
                <span className="partner-summary-card__value">{tile.value}</span>
                <span
                  className="partner-summary-card__dot"
                  style={{ backgroundColor: tile.color }}
                  aria-hidden
                />
              </span>
            </button>
          ))}
        </div>

        <section
          className="integration-platform-slider-row"
          aria-label="Integration platforms"
        >
          <Button
            appearance="outlined"
            disabled={!sliderHasOverflow || sliderAtStart}
            className="integration-platform-scroll-btn integration-platform-scroll-btn--prev"
            onClick={() => scrollPlatformSlider(-1)}
            aria-label="Scroll platforms left"
            label={
              <Iconography icon="angleLeft" size={IconographySizes.Small} />
            }
          />
          <div
            ref={sliderTrackRef}
            className={`integration-platform-scroll${
              platformCards.length > 0 && platformCards.length < 4
                ? " integration-platform-scroll--center"
                : ""
            }`}
            role="list"
          >
            {platformCards.map((card) => (
              <IntegrationPlatformCardView
                key={card.id}
                card={card}
                isSelected={selectedTechnologyKeys.includes(card.id)}
                onSelect={() => handlePlatformCardClick(card.id)}
              />
            ))}
          </div>
          <Button
            appearance="outlined"
            disabled={!sliderHasOverflow || sliderAtEnd}
            className="integration-platform-scroll-btn integration-platform-scroll-btn--next"
            onClick={() => scrollPlatformSlider(1)}
            aria-label="Scroll platforms right"
            label={
              <Iconography icon="angleRight" size={IconographySizes.Small} />
            }
          />
        </section>
      </div>

      <div className="partner-toolbar">
        <div className="search-input-filter-wrapper">
          <div className="left">
            <SearchInput
              theme="dark"
              id="searchIntegrationPartner"
              name="searchIntegrationPartner"
              placeholder="Search Partner by Name"
              isDisplayClearIcon={true}
              value={searchTerm}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(event.currentTarget.value)
              }
              onSearch={handleSearch}
            />
          </div>
          <div className="center">
            <button
              type="button"
              className={`toolbar-link filter-link${
                hasActiveFilters ? " toolbar-link--active" : ""
              }`}
              onClick={() => setFilterModalOpen(true)}
              disabled={integrationStatusReadOnly}
            >
              <Iconography icon="filter" size={IconographySizes.Small} />
              <span>Filter</span>
            </button>
            <IntegrationColumnsDropdown
              availableColumns={tableColumns}
              visibleColumnKeys={visibleColumnKeys}
              onVisibleColumnKeysChange={handleVisibleColumnKeysChange}
            />
          </div>
          <div className="right">
            {canAccessIntegrationStatus && (
              <Button
                variant="primary"
                label="Export"
                icon={<Iconography icon="arrowUpFromSquare" />}
                disabled={integrationStatusReadOnly || status.exportLoading}
                onClick={handleExport}
              />
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filter-tag-wrapper">
            {integrationFilterTags.map((tag) => (
              <Tag
                key={tag.key}
                value={tag.label}
                appearance={TagAppearance.Blue}
                isCloseIcon={true}
                onCloseClick={() => handleRemoveIntegrationFilterTag(tag.key)}
              />
            ))}
          </div>
        )}
      </div>

      {filterModalOpen && (
        <IntegrationFilterModal
          integrationFilterProp={integrationFilter}
          setIntegrationFilterProp={handleApplyIntegrationFilter}
          setModalOpen={setFilterModalOpen}
        />
      )}

      <div className="table-card integration-table-card">
        {status.error && (
          <div className="no-data integration-status__error">{status.error}</div>
        )}
        {visibleColumnKeys.length === 0 ? (
          <div className="no-data">
            Select at least one column to display the integration status table.
          </div>
        ) : showTableLoader ? (
          <div className="integration-status__table-loader">
            <Loader variant={LoaderSizes.medium} />
          </div>
        ) : (
          <>
            <div
              className="integration-table"
              style={
                {
                  ["--integration-col-count" as string]: visibleColumnKeys.length,
                } as React.CSSProperties
              }
            >
              <div className="integration-table__header">
                <div
  className="integration-table__header-partner"
  style={{
    gridColumn: 1,
    cursor: "pointer",
  }}
  onClick={handlePartnerNameSort}
>
  Partner Name {getSortIcon()}
</div>
                {visibleColumnKeys.map((colKey, columnIndex) => (
                  <div
                    className="integration-table__header-platform"
                    key={colKey}
                    style={{ gridColumn: columnIndex + 2 }}
                  >
                    {formatIntegrationColumnLabel(colKey, tableColumns)}
                  </div>
                ))}
              </div>

              {partners.length === 0 ? (
                <div className="no-data">
                  {appliedSearch
                    ? "No partners match your search."
                    : "No data available for display."}
                </div>
              ) : (
                sortedPartners.map((partner) => (
                  <IntegrationPartnerBlock
                    key={partner.id}
                    partner={partner}
                    visibleColumnKeys={visibleColumnKeys}
                    isExpanded={expandedPartnerIds.has(partner.id)}
                    onToggleExpand={() => togglePartnerExpanded(partner.id)}
                    onOpenPartner={onOpenPartner}
                    canOpenPartner={canOpenPartner}
                    onViewProjects={handleViewPartnerProjects}
                    onViewProject={handleViewPartnerProject}
                    viewProjectsEnabled={viewAllProjectsEnabled}
                  />
                ))
              )}
            </div>
            {pagination && pagination.total_records > 0 && (
              <Pagination
                currentPageNumber={pagination.current_page}
                totalRecords={pagination.total_records}
                pageSize={pagination.per_page}
                onPageNumberClick={handlePageChange}
                onPageSizesClick={onPageSizesClick}
                pageSizes={[
                  { label: "20", value: "20" },
                  { label: "50", value: "50" },
                  { label: "100", value: "100" },
                ]}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const CURRENT_STATUS_LABELS: Record<IntegrationCurrentStatus, string> = {
  completed: "Completed",
  "in-progress": "In Progress",
  "on-hold": "On Hold",
  "not-started": "Not Started",
  "not-applicable": "Not Applicable",
  "stage-completed": "Completed",
};

const HIDDEN_INTEGRATION_STATUSES: IntegrationCurrentStatus[] = [
  "not-started",
  "not-applicable",
];

const isHiddenIntegrationBlock = (status: IntegrationCurrentStatus): boolean =>
  HIDDEN_INTEGRATION_STATUSES.includes(status);

const filterVisibleIntegrationProjects = (
  details: IntegrationPlatformDetail[]
): IntegrationPlatformDetail[] =>
  details
    .map((detail) => ({
      ...detail,
      projects: detail.projects.filter(
        (project) => !isHiddenIntegrationBlock(project.currentStatus)
      ),
    }))
    .filter((detail) => detail.projects.length > 0);

const IntegrationCurrentStatusCell: React.FC<{
  currentStatus: IntegrationCurrentStatus;
  subTask?: string;
}> = ({ currentStatus, subTask }) => {
  if (currentStatus === "completed") {
    return (
      <div className="integration-current-status integration-current-status--completed">
        <Iconography
          icon="circleCheck"
          size={IconographySizes.Small}
          className="integration-current-status__icon"
          aria-hidden
        />
        <span className="integration-current-status__text">
          {CURRENT_STATUS_LABELS.completed}
        </span>
      </div>
    );
  }

  if (
    (currentStatus === "not-started" || currentStatus === "not-applicable") &&
    !subTask
  ) {
    return (
      <div className="integration-current-status integration-current-status--active">
        <span className="integration-current-status__value">
          {CURRENT_STATUS_LABELS[currentStatus]}
        </span>
      </div>
    );
  }

  if (currentStatus === "stage-completed" && subTask) {
    return (
      <div className="integration-current-status integration-current-status--completed">
        <Iconography
          icon="circleCheck"
          size={IconographySizes.Small}
          className="integration-current-status__icon"
          aria-hidden
        />
        <span className="integration-current-status__text">
          {subTask}: {CURRENT_STATUS_LABELS["stage-completed"]}
        </span>
      </div>
    );
  }

  return (
    <div className="integration-current-status integration-current-status--active">
      <span className="integration-current-status__task">{subTask ?? "—"}:</span>{" "}
      <span className="integration-current-status__value">
        {CURRENT_STATUS_LABELS[currentStatus]}
      </span>
    </div>
  );
};

const IntegrationServiceStatusBar: React.FC<{
  project: IntegrationProjectDetail;
}> = ({ project }) => (
  <div className="integration-status-bar">
    <div className="integration-status-bar__section integration-status-bar__section--age">
      <span className="integration-status-bar__label">INTEGRATION AGE</span>
      <div className="integration-status-bar__value-row">
        <span className="integration-status-bar__age">
          <span className="integration-status-bar__age-label">Age:</span>{" "}
          <span className="integration-status-bar__age-value">{project.age}</span>
        </span>
      </div>
    </div>
    <span className="integration-status-bar__divider" aria-hidden />
    <div className="integration-status-bar__section integration-status-bar__section--current-status">
      <span className="integration-status-bar__label">CURRENT STATUS</span>
      <div className="integration-status-bar__value-row">
        <IntegrationCurrentStatusCell
          currentStatus={project.currentStatus}
          subTask={project.currentStatusSubTask}
        />
      </div>
    </div>
    <span className="integration-status-bar__divider" aria-hidden />
    <div className="integration-status-bar__section integration-status-bar__section--endorsement">
      <span className="integration-status-bar__label">ENDORSEMENT</span>
      <div className="integration-status-bar__value-row">
        {project.endorsement.variant === "neutral" ? (
          <span className="integration-status-bar__dash">{project.endorsement.label}</span>
        ) : (
          <span
            className={`integration-endorsement integration-endorsement--${project.endorsement.variant}`}
          >
            {project.endorsement.label}
          </span>
        )}
      </div>
    </div>
    <span className="integration-status-bar__divider" aria-hidden />
    <div className="integration-status-bar__section integration-status-bar__section--score">
      <span className="integration-status-bar__label">SCORE</span>
      <div className="integration-status-bar__value-row">
        <span className="integration-status-bar__score">{project.score}</span>
      </div>
    </div>
  </div>
);

const IntegrationExpandedServiceRow: React.FC<{
  project: IntegrationProjectDetail;
  partnerId: number;
  technology?: string;
  viewProjectsEnabled: boolean;
  onViewProject: (
    partnerId: number,
    projectName: string,
    technology?: string
  ) => void;
}> = ({ project, partnerId, technology, viewProjectsEnabled, onViewProject }) => (
  <div className="integration-expanded-service">
    <div className="integration-expanded-service__info">
      {viewProjectsEnabled ? (
        <button
          type="button"
          className="integration-expanded-service__name integration-expanded-service__name--link"
          onClick={() => onViewProject(partnerId, project.serviceName, technology)}
        >
          {project.serviceName}
        </button>
      ) : (
        <div className="integration-expanded-service__name">{project.serviceName}</div>
      )}
      <div className="integration-expanded-service__type">
        {project.provisioningType}
      </div>
      <TechnicalConsultantInfo
        name={project.technicalConsultant}
        className="integration-expanded-service__consultant"
      />
    </div>
    <div className="integration-expanded-service__content">
      <IntegrationTimeline milestones={project.milestones} />
      <IntegrationServiceStatusBar project={project} />
    </div>
  </div>
);

const IntegrationExpandedPlatformSection: React.FC<{
  detail: IntegrationPlatformDetail;
  partnerId: number;
  viewProjectsEnabled: boolean;
  onViewProjects: (partnerId: number) => void;
  onViewProject: (
    partnerId: number,
    projectName: string,
    technology?: string
  ) => void;
}> = ({
  detail,
  partnerId,
  viewProjectsEnabled,
  onViewProjects,
  onViewProject,
}) => (
  <section className="integration-expanded-platform">
    <div className="integration-expanded-platform__card">
      <div className="integration-expanded-platform__header">
        <div className="integration-expanded-platform__header-main">
          <h3 className="integration-expanded-platform__title">{detail.platformLabel}</h3>
          <span className="integration-expanded-platform__badge">
            {detail.projects.length} project{detail.projects.length === 1 ? "" : "s"}
          </span>
        </div>
        <button
          type="button"
          className={`integration-expanded-platform__view-details${
            !viewProjectsEnabled ? " integration-expanded-platform__view-details--disabled" : ""
          }`}
          onClick={() => onViewProjects(partnerId)}
          disabled={!viewProjectsEnabled}
          aria-disabled={!viewProjectsEnabled}
        >
          View Details
        </button>
      </div>
      <div className="integration-expanded-platform__services">
        {detail.projects.map((project) => (
          <IntegrationExpandedServiceRow
            key={`${detail.platformKey}-${project.serviceName}-${project.provisioningType}`}
            project={project}
            partnerId={partnerId}
            technology={detail.platformLabel}
            viewProjectsEnabled={viewProjectsEnabled}
            onViewProject={onViewProject}
          />
        ))}
      </div>
    </div>
  </section>
);

const IntegrationExpandedPanel: React.FC<{
  details: IntegrationPlatformDetail[];
  partnerId: number;
  viewProjectsEnabled: boolean;
  onViewProjects: (partnerId: number) => void;
  onViewProject: (
    partnerId: number,
    projectName: string,
    technology?: string
  ) => void;
}> = ({
  details,
  partnerId,
  viewProjectsEnabled,
  onViewProjects,
  onViewProject,
}) => (
  <div className="integration-expanded-panel">
    {details.map((detail) => (
      <IntegrationExpandedPlatformSection
        key={detail.platformKey}
        detail={detail}
        partnerId={partnerId}
        viewProjectsEnabled={viewProjectsEnabled}
        onViewProjects={onViewProjects}
        onViewProject={onViewProject}
      />
    ))}
  </div>
);

const IntegrationPlatformCardView: React.FC<{
  card: IntegrationPlatformCard;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ card, isSelected, onSelect }) => (
  <button
    type="button"
    className={`integration-platform-card${
      isSelected ? " integration-platform-card--selected" : ""
    }`}
    onClick={onSelect}
    aria-pressed={isSelected}
    aria-label={`Filter table by ${card.name}`}
  >
    <div className="integration-platform-card__header">
      <span className="integration-platform-card__name">{card.name}</span>
      <span className="integration-platform-card__meta">
        <span>
          {card.integrationCount} Integration
          {card.integrationCount === 1 ? "" : "s"}
        </span>
        <span className="integration-platform-card__percent">
          {card.completionPercent}%
        </span>
      </span>
    </div>
    <div className="integration-platform-card__stats">
      <div className="integration-platform-card__stat integration-platform-card__stat--done">
        <span className="integration-platform-card__stat-value">{card.done}</span>
        <span className="integration-platform-card__stat-label">DONE</span>
      </div>
      <div className="integration-platform-card__stat integration-platform-card__stat--active">
        <span className="integration-platform-card__stat-value">{card.active}</span>
        <span className="integration-platform-card__stat-label">IN PROGRESS</span>
      </div>
      <div className="integration-platform-card__stat integration-platform-card__stat--hold">
        <span className="integration-platform-card__stat-value">{card.hold}</span>
        <span className="integration-platform-card__stat-label">HOLD</span>
      </div>
    </div>
  </button>
);

type IntegrationPartnerBlockProps = {
  partner: IntegrationPartnerRow;
  visibleColumnKeys: string[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onOpenPartner?: (partnerId: number) => void;
  canOpenPartner?: boolean;
  viewProjectsEnabled?: boolean;
  onViewProjects?: (partnerId: number) => void;
  onViewProject?: (
    partnerId: number,
    projectName: string,
    technology?: string
  ) => void;
};

const IntegrationPartnerBlock: React.FC<IntegrationPartnerBlockProps> = ({
  partner,
  visibleColumnKeys,
  isExpanded,
  onToggleExpand,
  onOpenPartner,
  canOpenPartner = false,
  viewProjectsEnabled = false,
  onViewProjects,
  onViewProject,
}) => {
  const visibleDetails = filterVisibleIntegrationProjects(
    (partner.expandedDetails ?? []).filter((detail) =>
      visibleColumnKeys.includes(detail.columnKey)
    )
  );

  return (
  <div className="integration-table__partner-group">
    <div
      className={`integration-table__partner-summary${isExpanded ? " is-expanded" : ""}`}
    >
      <div className="integration-table__partner-cell">
        <button
          type="button"
          className="integration-table__expand-btn"
          onClick={onToggleExpand}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? "Collapse" : "Expand"} ${partner.partnerName}`}
        >
          <Iconography
            icon={isExpanded ? "angleUp" : "angleDown"}
            size={IconographySizes.Small}
          />
        </button>
        <button
          type="button"
          className="integration-table__partner-name"
          onClick={() => onOpenPartner?.(partner.partnerId)}
          disabled={!canOpenPartner}
        >
          {partner.partnerName}
        </button>
      </div>

      {visibleColumnKeys.map((colKey, columnIndex) => (
        <div
          className="integration-table__status-cell"
          key={colKey}
          style={{ gridColumn: columnIndex + 2 }}
        >
          <IntegrationPlatformProgressCell
            progress={partner.platformProgress[colKey] ?? NOT_APPLICABLE_PROGRESS}
          />
        </div>
      ))}
    </div>

    {isExpanded && (
      <div className="integration-table__expanded">
        {visibleDetails.length > 0 ? (
          <IntegrationExpandedPanel
            details={visibleDetails}
            partnerId={partner.partnerId}
            viewProjectsEnabled={viewProjectsEnabled}
            onViewProjects={onViewProjects ?? (() => undefined)}
            onViewProject={onViewProject ?? (() => undefined)}
          />
        ) : (
          <div className="integration-expanded-empty">
            No integrations available for the selected platform.
          </div>
        )}
      </div>
    )}
  </div>
  );
};

export default IntegrationStatusTab;
